
import axios from "axios";
import { useAuth } from "../../../context/authProviderContext";
import "../sections/styles/profile.scss";
import React, { useEffect, useState, useRef, useCallback } from 'react';
import SmartInput from "../../../components/globalComponents/smartInput/smartInput";

// Creamos una instancia de axios con la configuración base
const api = axios.create({
  baseURL: "https://okapi-woocommerc-wr9i20lbrp.live-website.com/wp-json"
});

// Añadimos un interceptor para incluir el token JWT en las peticiones
api.interceptors.request.use(config => {
  const token = sessionStorage.getItem("jwt_token");
  if (token) {
    config.headers.Authorization = `Bearer ${token}`;
  }
  return config;
});

// Función auxiliar para extraer valores considerando diferentes nombres de campos
const getFieldValue = (source, fieldNames, defaultValue = "") => {
  if (!source) return defaultValue;
  
  // Si solo se pasa un nombre de campo, convertirlo en array
  const names = Array.isArray(fieldNames) ? fieldNames : [fieldNames];
  
  // Recorrer todos los posibles nombres del campo
  for (const name of names) {
    if (source[name] !== undefined && source[name] !== null && source[name] !== '') {
      return source[name];
    }
  }
  
  return defaultValue;
};

// Componente para el input de contraseña con toggle de visibilidad usando iconos de Bootstrap
const PasswordInput = ({ label, value, onChange, required = false, placeholder = "", minLength }) => {
  const [showPassword, setShowPassword] = useState(false);
  
  const togglePasswordVisibility = (e) => {
    e.preventDefault();
    setShowPassword(!showPassword);
  };
  
  return (
    <div className="form-row">
      <label>{label} {required && <span className="required">*</span>}</label>
      <div className="password-input-container">
        <input
          type={showPassword ? "text" : "password"}
          value={value}
          onChange={onChange}
          required={required}
          placeholder={placeholder}
          minLength={minLength}
          className="password-input"
        />
        <button 
          type="button"
          className="password-toggle-btn"
          onClick={togglePasswordVisibility}
          aria-label={showPassword ? "Ocultar contraseña" : "Mostrar contraseña"}
        >
          {showPassword ? (
            <i className="bi bi-eye-slash toggle-visibility"></i>
          ) : (
            <i className="bi bi-eye toggle-visibility"></i>
          )}
        </button>
      </div>
      {minLength && <small>Mínimo {minLength} caracteres</small>}
    </div>
  );
};

const Profile = () => {
  // Referencia al contexto global de autenticación
  const { user: authUser, setUser: updateAuthUser, refreshUserData } = useAuth();
  
  // Estado local para el formulario - INDEPENDIENTE del contexto global
  const [formData, setFormData] = useState({
    first_name: "",
    last_name: "",
    email: "",
    phone: "",
    company: "",
    country: "",
    state: "",
    city: "",
    job: "",
  });
  
  // Estado para gestionar cambio de contraseña
  const [passwordData, setPasswordData] = useState({
    current_password: "",
    new_password: "",
    confirm_password: ""
  });
  
  // Estados para controlar mensajes y carga
  const [passwordMessage, setPasswordMessage] = useState('');
  const [passwordStatus, setPasswordStatus] = useState('');
  const [isSaving, setIsSaving] = useState(false);
  const [saveMessage, setSaveMessage] = useState('');
  const [saveStatus, setSaveStatus] = useState('');
  const [isChangingPassword, setIsChangingPassword] = useState(false);
  const [isLoading, setIsLoading] = useState(true);
  const [hasError, setHasError] = useState(false);
  const [metadataStatus, setMetadataStatus] = useState('pending');
  
  // Referencia para rastrear si ya se ha intentado cargar datos
  const dataInitialized = useRef(false);
  
  // Verificar si es un registro reciente
  const isNewRegistration = useRef(sessionStorage.getItem("new_registration") === "true");
  
  // Referencia para loadFormData para evitar dependencia circular
  const loadFormDataRef = useRef(null);

  // Validaciones
  const validateFields = () => {
    const requiredFields = ['first_name', 'last_name', 'email'];
    return requiredFields.every(field => formData[field]?.trim());
  };

  const validatePassword = () => {
    if (!passwordData.current_password) {
      setPasswordMessage('La contraseña actual es obligatoria');
      setPasswordStatus('error');
      return false;
    }
    
    if (!passwordData.new_password) {
      setPasswordMessage('La nueva contraseña es obligatoria');
      setPasswordStatus('error');
      return false;
    }
    
    if (passwordData.new_password !== passwordData.confirm_password) {
      setPasswordMessage('Las contraseñas no coinciden');
      setPasswordStatus('error');
      return false;
    }
    
    if (passwordData.new_password.length < 8) {
      setPasswordMessage('La contraseña debe tener al menos 8 caracteres');
      setPasswordStatus('error');
      return false;
    }
    
    return true;
  };

  // SOLUCIÓN: Primero definimos handleForceReload usando la referencia
  const handleForceReload = useCallback(async () => {
    setMetadataStatus('loading');
    
    try {
      console.log("🔄 Forzando recarga de datos...");
      
      // Si refreshUserData está disponible, usarlo
      let refreshed = false;
      
      if (refreshUserData && typeof refreshUserData === 'function') {
        try {
          await refreshUserData();
          console.log("✅ Datos actualizados desde el servidor");
          refreshed = true;
        } catch (error) {
          console.error("❌ Error al actualizar datos desde el servidor:", error);
        }
      }
      
      // Si estábamos en un registro nuevo, quitar la marca
      if (isNewRegistration.current) {
        console.log("✅ Procesamiento de registro nuevo completado");
        sessionStorage.removeItem("new_registration");
        isNewRegistration.current = false;
      }
      
      // Cargar datos usando la referencia en lugar de la función directamente
      if (loadFormDataRef.current) {
        loadFormDataRef.current();
      }
      
      setMetadataStatus('success');
      return refreshed;
    } catch (error) {
      console.error("❌ Error al forzar recarga:", error);
      setMetadataStatus('error');
      return false;
    }
  }, [refreshUserData]); // Sin incluir loadFormData en dependencias

  // Ahora definimos loadFormData normalmente
  const loadFormData = useCallback(() => {
    // Intentamos obtener datos del usuario con prioridad en velocidad
    let userData = null;
    
    // Verificar si es un registro reciente
    if (isNewRegistration.current) {
      console.log("🆕 Registro reciente detectado en Profile, priorizando contexto auth");
    }
    
    // Intentar usar datos del contexto global primero (para registros recientes)
    if (authUser && authUser.email) {
      userData = authUser;
      console.log("🔑 Datos obtenidos del contexto de autenticación:", userData);
    }
    
    // Si no hay datos en el contexto o NO es registro reciente, probar localStorage
    if ((!userData || !isNewRegistration.current)) {
      try {
        const storedData = localStorage.getItem("user_data");
        if (storedData && storedData !== "null") {
          const parsedData = JSON.parse(storedData);
          if (parsedData && parsedData.email) {
            userData = parsedData;
            console.log("📋 Datos obtenidos de localStorage:", userData);
          }
        }
      } catch (error) {
        console.error("❌ Error al leer localStorage:", error);
      }
    }
    
    // Mostrar un error solo si realmente no hay datos disponibles
    // y NO estamos en un registro reciente (podría estar cargando datos)
    if (!userData) {
      if (isNewRegistration.current) {
        console.log("⏳ Esperando datos para usuario recién registrado...");
        return false;
      }
      
      console.error("❌ No se encontraron datos de usuario en ninguna fuente");
      setHasError(true);
      setIsLoading(false);
      return false;
    }
    
    // Crear objeto de datos para el formulario
    const newFormData = {
      first_name: getFieldValue(userData, ['firstName', 'first_name']),
      last_name: getFieldValue(userData, ['lastName', 'last_name']),
      email: userData.email || "",
      phone: getFieldValue(userData, ['phone']),
      company: getFieldValue(userData, ['company']),
      country: getFieldValue(userData, ['country']),
      state: getFieldValue(userData, ['state']),
      city: getFieldValue(userData, ['city']),
      job: getFieldValue(userData, ['job']),
    };
    
    // Actualizar el UI inmediatamente
    setFormData(newFormData);
    setIsLoading(false);
    dataInitialized.current = true;
    
    // Verificar si tenemos todos los metadatos
    const metaFields = ['phone', 'company', 'country', 'state', 'city', 'job'];
    const foundFields = metaFields.filter(field => !!newFormData[field]);
    
    // Solo para depuración
    console.log(`✅ Campos de metadatos encontrados: ${foundFields.length} de ${metaFields.length}`, foundFields);
    
    // Retornamos true si todos los campos están presentes, false si faltan campos
    return foundFields.length === metaFields.length;
  }, [authUser]);

  // Actualizamos la referencia de loadFormData cuando cambie
  useEffect(() => {
    loadFormDataRef.current = loadFormData;
  }, [loadFormData]);

  // Efecto principal para cargar datos
  useEffect(() => {
    // Si ya se intentó cargar los datos, no hacemos nada
    if (dataInitialized.current) {
      return;
    }
    
    const loadUserData = async () => {
      try {
        setIsLoading(true);
        
        // Registro nuevo: forzar carga desde servidor inmediatamente
        if (isNewRegistration.current) {
          console.log("🆕 Detectado registro nuevo, obteniendo datos frescos...");
          
          // Pequeño retraso para asegurar que el AuthProvider tenga tiempo
          setTimeout(() => {
            handleForceReload().then(success => {
              if (!success) {
                // Intentar cargar datos locales como fallback
                loadFormData();
              }
            });
          }, 500); // Aumentamos a 500ms para dar tiempo al servidor
          
          return;
        }
        
        // Flujo normal: intentar carga local primero
        const allFieldsLoaded = loadFormData();
        
        // Si faltan campos, intentar obtenerlos desde el servidor
        if (!allFieldsLoaded && refreshUserData && typeof refreshUserData === 'function') {
          console.log("⚠️ Faltan algunos campos, intentando cargar desde servidor...");
          
          setTimeout(() => {
            handleForceReload();
          }, 100);
        }
      } catch (error) {
        console.error("❌ Error al cargar datos de usuario:", error);
        setHasError(true);
        setIsLoading(false);
      }
    };
    
    loadUserData();
  }, [handleForceReload, loadFormData, refreshUserData]);

  // Efecto para actualizar el formulario cuando cambia el usuario autenticado
  useEffect(() => {
    // Si el authUser cambia después de la carga inicial, actualizar formData
    if (authUser && dataInitialized.current) {
      console.log("👤 Usuario autenticado cambió, actualizando formulario...");
      
      setFormData(prevData => ({
        ...prevData,
        first_name: getFieldValue(authUser, ['firstName', 'first_name']) || prevData.first_name,
        last_name: getFieldValue(authUser, ['lastName', 'last_name']) || prevData.last_name,
        email: authUser.email || prevData.email,
        phone: getFieldValue(authUser, ['phone']) || prevData.phone,
        company: getFieldValue(authUser, ['company']) || prevData.company,
        country: getFieldValue(authUser, ['country']) || prevData.country,
        state: getFieldValue(authUser, ['state']) || prevData.state,
        city: getFieldValue(authUser, ['city']) || prevData.city,
        job: getFieldValue(authUser, ['job']) || prevData.job
      }));
    }
  }, [authUser]);

  // Manejamos el guardado de los cambios del perfil
  const handleSave = async (e) => {
    e.preventDefault();
    
    if (!validateFields()) {
      setSaveMessage('Por favor completa los campos requeridos');
      setSaveStatus('error');
      return;
    }

    setIsSaving(true);
    setSaveMessage('');
    setSaveStatus('');
    
    try {
      // Preparamos los datos para enviar
      const dataToSend = {
        ...formData,
        phone: formData.phone || "",
        company: formData.company || "",
        country: formData.country || "",
        state: formData.state || "",
        city: formData.city || "",
        job: formData.job || "",
      };
      
      console.log("📤 Enviando datos al servidor:", dataToSend);
      
      // Enviamos los datos actualizados al endpoint
      const response = await api.post("/custom/v1/update-profile", dataToSend);

      console.log("📥 Respuesta completa del servidor:", response);
      console.log("✅ Datos de usuario actualizados:", response.data);
      
      if (response.data.success && response.data.user) {
        // Obtenemos los datos actualizados del servidor
        const updatedUserData = response.data.user;
        
        // Creamos un objeto completo combinando los datos previos con los nuevos
        const baseUser = authUser || {};
        const completeUser = {
          ...baseUser,
          ...updatedUserData,
          // Garantizamos consistencia de nombres de campos
          firstName: updatedUserData.firstName || updatedUserData.first_name || formData.first_name,
          lastName: updatedUserData.lastName || updatedUserData.last_name || formData.last_name,
          // Y de campos personalizados
          phone: updatedUserData.phone || formData.phone,
          company: updatedUserData.company || formData.company,
          country: updatedUserData.country || formData.country,
          state: updatedUserData.state || formData.state,
          city: updatedUserData.city || formData.city,
          job: updatedUserData.job || formData.job,
        };
        
        console.log("💾 Guardando usuario completo:", completeUser);
        
        // Actualizamos localStorage y el contexto global
        localStorage.setItem('user_data', JSON.stringify(completeUser));
        localStorage.setItem('user_email', completeUser.email);
        
        // Solo actualizamos el contexto global si la función está disponible
        if (updateAuthUser && typeof updateAuthUser === 'function') {
          updateAuthUser(completeUser);
        }
        
        // Actualizamos el formulario con los datos más recientes
        setFormData({
          first_name: completeUser.firstName || completeUser.first_name || "",
          last_name: completeUser.lastName || completeUser.last_name || "",
          email: completeUser.email || "",
          phone: completeUser.phone || "",
          company: completeUser.company || "",
          country: completeUser.country || "",
          state: completeUser.state || "",
          city: completeUser.city || "",
          job: completeUser.job || "",
        });
        
        // Mostramos mensaje de éxito
        setSaveMessage('¡Perfil actualizado correctamente!');
        setSaveStatus('success');
      } else {
        throw new Error('No se recibieron datos de usuario actualizados');
      }
    } catch (error) {
      console.error("❌ Error al actualizar el perfil:", error);
      
      if (error.response) {
        console.error("❌ Respuesta de error:", error.response.data);
        console.error("❌ Estado HTTP:", error.response.status);
      }
      
      let errorMessage = 'Error al actualizar el perfil';
      if (error.response?.data?.message) {
        errorMessage += `: ${error.response.data.message}`;
      } else if (error.message) {
        errorMessage += `: ${error.message}`;
      }
      
      setSaveMessage(errorMessage);
      setSaveStatus('error');
    } finally {
      setIsSaving(false);
    }
  };

  // Manejamos el cambio de contraseña
  const handleChangePassword = async (e) => {
    e.preventDefault();
    
    if (!validatePassword()) {
      return;
    }

    setIsChangingPassword(true);
    setPasswordMessage('');
    setPasswordStatus('');
    
    try {
      // Preparamos los datos para enviar
      const passwordPayload = {
        current_password: passwordData.current_password,
        new_password: passwordData.new_password
      };
      
      console.log("📤 Enviando datos de contraseña...");
      
      // Enviamos la petición para cambiar la contraseña
      const response = await api.post("/custom/v1/change-password", passwordPayload);
      
      console.log("✅ Respuesta cambio de contraseña:", response.data);
      
      if (response.data.success) {
        setPasswordMessage('¡Contraseña actualizada correctamente!');
        setPasswordStatus('success');
        
        // Limpiamos los campos de contraseña
        setPasswordData({
          current_password: "",
          new_password: "",
          confirm_password: ""
        });
      } else {
        throw new Error(response.data.message || 'Error al cambiar la contraseña');
      }
    } catch (error) {
      console.error("❌ Error al cambiar contraseña:", error);
      
      // Verificamos si es un error de Axios con respuesta del servidor
      if (error.response && error.response.data) {
        console.error("❌ Detalles del error:", error.response.data);
        
        // Extraemos el mensaje específico del servidor
        const serverMessage = error.response.data.message || 
                           (error.response.data.code === 'wrong_password' ? 'La contraseña actual es incorrecta' : null);
        
        if (serverMessage) {
          setPasswordMessage(serverMessage);
          setPasswordStatus('error');
          return;
        }
      }
      
      // Si llegamos aquí, usamos el mensaje genérico o el mensaje del error
      let errorMessage = 'Error al cambiar la contraseña';
      if (error.message && !error.message.includes('status code')) {
        errorMessage += `: ${error.message}`;
      }
      
      setPasswordMessage(errorMessage);
      setPasswordStatus('error');
    } finally {
      setIsChangingPassword(false);
    }
  };

  // Manejamos el cambio de los inputs del formulario
  const handleInputChange = (e) => {
    const { name, value } = e.target;
    setFormData(prev => ({ ...prev, [name]: value }));
  };

  // Componente de depuración para desarrollo
  const DebugPanel = () => {
    // Valores que el formulario está utilizando
    const formValues = {
      first_name: formData.first_name,
      last_name: formData.last_name,
      email: formData.email,
      phone: formData.phone,
      company: formData.company,
      country: formData.country,
      state: formData.state,
      city: formData.city,
      job: formData.job
    };
    
    // Valores que existen en authUser (el contexto global)
    const authValues = authUser ? {
      id: authUser.id,
      firstName: authUser.firstName,
      lastName: authUser.lastName,
      email: authUser.email,
      phone: authUser.phone,
      company: authUser.company,
      country: authUser.country,
      state: authUser.state,
      city: authUser.city,
      job: authUser.job
    } : 'No hay usuario autenticado';
    
    // Datos en localStorage
    let localStorageValues = 'No disponible';
    try {
      const storedData = localStorage.getItem('user_data');
      if (storedData) {
        const parsedData = JSON.parse(storedData);
        localStorageValues = {
          id: parsedData.id,
          firstName: parsedData.firstName,
          lastName: parsedData.lastName,
          email: parsedData.email,
          phone: parsedData.phone,
          company: parsedData.company,
          country: parsedData.country,
          state: parsedData.state,
          city: parsedData.city,
          job: parsedData.job
        };
      }
    } catch (e) {
      localStorageValues = `Error: ${e.message}`;
    }
    
    // Información de registro reciente
    const registrationInfo = {
      isNewRegistration: isNewRegistration.current,
      sessionStorageFlag: sessionStorage.getItem("new_registration") === "true"
    };
    
    return (
      <div className="debug-panel" style={{ 
        border: '1px dashed #ccc', 
        margin: '20px 0', 
        padding: '15px',
        backgroundColor: '#f9f9f9' 
      }}>
        <h3 style={{ borderBottom: '1px solid #eee', paddingBottom: '10px' }}>Panel de depuración</h3>
        
        <div style={{ marginBottom: '15px' }}>
          <button 
            onClick={handleForceReload}
            style={{
              padding: '8px 12px',
              backgroundColor: '#4CAF50',
              color: 'white',
              border: 'none',
              borderRadius: '4px',
              marginRight: '10px'
            }}
            disabled={metadataStatus === 'loading'}
          >
            {metadataStatus === 'loading' ? 'Recargando...' : 'Forzar recarga de datos'}
          </button>
          
          <button 
            onClick={() => {
              console.log("Formulario:", formValues);
              console.log("Auth Context:", authValues);
              console.log("localStorage:", localStorageValues);
              console.log("Registro:", registrationInfo);
            }}
            style={{
              padding: '8px 12px',
              backgroundColor: '#007bff',
              color: 'white',
              border: 'none',
              borderRadius: '4px'
            }}
          >
            Mostrar datos en consola
          </button>
        </div>
        
        <div style={{ display: 'flex', flexWrap: 'wrap', gap: '20px' }}>
          <div style={{ flex: '1', minWidth: '300px' }}>
            <h4>Valores del formulario:</h4>
            <pre style={{ 
              backgroundColor: '#eee', 
              padding: '10px', 
              overflow: 'auto',
              fontSize: '12px'
            }}>
              {JSON.stringify(formValues, null, 2)}
            </pre>
          </div>
          
          <div style={{ flex: '1', minWidth: '300px' }}>
            <h4>Valores del contexto global:</h4>
            <pre style={{ 
              backgroundColor: '#eee', 
              padding: '10px', 
              overflow: 'auto',
              fontSize: '12px'
            }}>
              {typeof authValues === 'string' 
                ? authValues 
                : JSON.stringify(authValues, null, 2)}
            </pre>
          </div>
          
          <div style={{ flex: '1', minWidth: '300px' }}>
            <h4>Valores de localStorage:</h4>
            <pre style={{ 
              backgroundColor: '#eee', 
              padding: '10px', 
              overflow: 'auto',
              fontSize: '12px'
            }}>
              {typeof localStorageValues === 'string' 
                ? localStorageValues 
                : JSON.stringify(localStorageValues, null, 2)}
            </pre>
          </div>
          
          <div style={{ flex: '1', minWidth: '300px' }}>
            <h4>Info de Registro:</h4>
            <pre style={{ 
              backgroundColor: '#eee', 
              padding: '10px', 
              overflow: 'auto',
              fontSize: '12px'
            }}>
              {JSON.stringify(registrationInfo, null, 2)}
            </pre>
          </div>
        </div>
      </div>
    );
  };

  // Estados de carga y error
  if (isLoading) {
    return (
      <div className="profile-loading">
        <div className="loading-spinner"></div>
        <p>Cargando perfil...</p>
      </div>
    );
  }
  
  if (hasError) {
    return (
      <div className="profile-error">
        <i className="bi bi-exclamation-circle"></i>
        <p>No se pudieron cargar los datos del perfil.</p>
        <button onClick={handleForceReload}>Intentar de nuevo</button>
      </div>
    );
  }

  return (
    <div className="profile-section">
      <h2>Your Profile</h2>
      <p>View or edit your personal information.</p>

      <form className="profile-form" onSubmit={handleSave}>
        <div className="form-row">
          <SmartInput
            type="text"
            as="input"
            label="Name"
            name="first_name"
            value={formData.first_name}
            onChange={handleInputChange}
            required
          />
        </div>

        <div className="form-row">
           <SmartInput
            type="text"
            as="input"
            label="Last Name"
            name="last_name"
            value={formData.last_name}
            onChange={handleInputChange}
            required
          />
        </div>

        <div className="form-row">
          <label>Email <span className="required">*</span></label>
          <input 
            type="email" 
            value={formData.email} 
            readOnly 
            className="readonly-field"
          />
          <small>El email no se puede modificar.</small>
        </div>

        <div className="form-row">
          <SmartInput
              type="tel"
              as="input"
              label="Phone"
              name="phone"
              value={formData.phone}
              onChange={handleInputChange}
              required
              placeholder="+34 623 456 789"
            />
        </div>

        <div className="form-row">
          <SmartInput
                type="text"
                as="input"
                label="Company"
                name="company"
                value={formData.company}
                onChange={handleInputChange}
                required
                placeholder="Nombre de tu empresa"
              />
        </div>

        <div className="form-row">
          <SmartInput
                  type="text"
                  as="input"
                  label="Country"
                  name="country"
                  value={formData.country}
                  onChange={handleInputChange}
                  required
                  placeholder="Ej: España"
                />
        </div>

        <div className="form-row">
          <SmartInput
                    type="text"
                    as="input"
                    label="State/Province"
                    name="state"
                    value={formData.state}
                    onChange={handleInputChange}
                    required
                    placeholder="Ej: Madrid"
            />
        </div>

        <div className="form-row">
          <SmartInput
              type="text"
              as="input"
              label="City"
              name="city"
              value={formData.city}
              onChange={handleInputChange}
              required
              placeholder="Ej: Getafe"
          />
        </div>

        <div className="form-row">
          <SmartInput
                type="text"
                as="input"
                label="Job"
                name="job"
                value={formData.job}
                onChange={handleInputChange}
                required
                placeholder="Ej: Web Developer"
            />
        </div>

        <div className="form-actions">
          <button 
            type="submit"
            disabled={isSaving || !validateFields()}
            className="save-button"
          >
            {isSaving ? 'Guardando...' : 'Guardar Cambios'}
          </button>
          
          {saveMessage && (
            <div className={`save-message ${saveStatus}`}>
              {saveStatus === 'error' ? (
                <>
                  <i className="bi bi-exclamation-triangle-fill error-icon"></i> {saveMessage}
                </>
              ) : (
                <>
                  <i className="bi bi-check-circle-fill success-icon"></i> {saveMessage}
                </>
              )}
            </div>
          )}
        </div>
      </form>

      {/* Sección de cambio de contraseña */}
      <div className="password-section">
        <h3>Change Password</h3>
        <form className="password-form" onSubmit={handleChangePassword}>
          {/* Contraseña actual */}
          <PasswordInput
            label="Current Password"
            value={passwordData.current_password}
            onChange={(e) => setPasswordData({ ...passwordData, current_password: e.target.value })}
            required={true}
          />

          {/* Nueva contraseña */}
          <PasswordInput
            label="New Password"
            value={passwordData.new_password}
            onChange={(e) => setPasswordData({ ...passwordData, new_password: e.target.value })}
            required={true}
            minLength={8}
          />

          {/* Confirmar nueva contraseña */}
          <PasswordInput
            label="Confirm New Password"
            value={passwordData.confirm_password}
            onChange={(e) => setPasswordData({ ...passwordData, confirm_password: e.target.value })}
            required={true}
          />

          <div className="form-actions">
            <button 
              type="submit"
              disabled={isChangingPassword || !passwordData.current_password || !passwordData.new_password || !passwordData.confirm_password}
              className="save-button"
            >
              {isChangingPassword ? 'Cambiando...' : 'Cambiar Contraseña'}
            </button>
            
            {passwordMessage && (
              <div className={`save-message ${passwordStatus}`}>
                {passwordStatus === 'error' ? (
                  <>
                    <i className="bi bi-exclamation-triangle-fill error-icon"></i> {passwordMessage}
                  </>
                ) : (
                  <>
                    <i className="bi bi-check-circle-fill success-icon"></i> {passwordMessage}
                  </>
                )}
              </div>
            )}
          </div>
        </form>
      </div>
      
      {/* Panel de depuración - solo visible en desarrollo */}
      {process.env.NODE_ENV !== 'production' && <DebugPanel />}
    </div>
  );
};

export default Profile;